import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { VENEZUELA_BANKS } from '../utils/bankCodes';
import { getBackendErrorMessage } from '../utils/toastError';
import useEscapeKey from '../hooks/useEscapeKey';

const FIRES_PER_USDT_DEFAULT = 100;

const RedeemModal = ({ isOpen, onClose, user, refreshUser, fiatContext, onBack }) => {
  useEscapeKey(onClose, isOpen);
  const queryClient = useQueryClient();
  const [redeemData, setRedeemData] = useState({
    cedula: '',
    telefono: '',
    bank_code: '',
    bank_name: '',
    fires_amount: 100,
    wallet_address: '',
    network: 'TRON'
  });
  const [payoutMethod, setPayoutMethod] = useState('bs'); // 'bs' | 'usdt_tron'
  const [otpStep, setOtpStep] = useState('idle'); // 'idle' | 'requested' | 'verifying'
  const [otpCode, setOtpCode] = useState('');
  const [hasPayoutData, setHasPayoutData] = useState(false);

  const firesPerUsdt = fiatContext?.config?.fires_per_usdt ?? FIRES_PER_USDT_DEFAULT;
  const bcvRate = fiatContext?.bcvRate?.rate ?? null;

  const amountNumber = parseFloat(redeemData.fires_amount || 0);
  const commissionAmount = amountNumber * 0.05;
  const totalRequiredAmount = amountNumber + commissionAmount;
  const estimatedUsdt = amountNumber > 0 ? amountNumber / firesPerUsdt : 0;
  const estimatedBs =
    payoutMethod === 'bs' && amountNumber > 0 && bcvRate
      ? (amountNumber / firesPerUsdt) * bcvRate
      : null;

  const banksSorted = useMemo(() => {
    return [...(Array.isArray(VENEZUELA_BANKS) ? VENEZUELA_BANKS : [])].sort((a, b) =>
      String(a?.name || '').localeCompare(String(b?.name || ''))
    );
  }, []);

  // Load payout data from profile when modal opens
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    let cancelled = false;
    const loadPayout = async () => {
      try {
        const res = await axios.get(`/api/profile/${user.id}/payout-data`);
        if (cancelled) return;
        const d = res.data;
        const hasData = !!(d.payout_cedula || d.payout_phone || d.payout_bank_code || d.payout_usdt_tron);
        setHasPayoutData(hasData);
        setRedeemData(prev => ({
          ...prev,
          cedula: d.payout_cedula || prev.cedula || '',
          telefono: d.payout_phone || prev.telefono || '',
          bank_code: d.payout_bank_code || prev.bank_code || '',
          bank_name: d.payout_bank_name || prev.bank_name || '',
          wallet_address: d.payout_usdt_tron || prev.wallet_address || ''
        }));
      } catch (err) {
        // silent fail
      }
    };
    loadPayout();
    return () => { cancelled = true; };
  }, [isOpen, user?.id]);

  // Reset OTP step when payout method changes
  useEffect(() => {
    setOtpStep('idle');
    setOtpCode('');
  }, [payoutMethod]);

  const redeemMutation = useMutation({
    mutationFn: async (data) => {
      return axios.post('/api/market/redeem-100-fire', data);
    },
    onSuccess: () => {
      toast.success('Solicitud de retiro creada exitosamente');
      setOtpStep('idle');
      setOtpCode('');
      onClose();
      setRedeemData({
        cedula: '',
        telefono: '',
        bank_code: '',
        bank_name: '',
        fires_amount: 100,
        wallet_address: '',
        network: 'TRON'
      });
      setPayoutMethod('bs');
      refreshUser?.();
      queryClient.invalidateQueries(['my-redeems']);
      queryClient.invalidateQueries(['wallet-transactions']);
      queryClient.invalidateQueries(['user-stats']);
      queryClient.invalidateQueries(['first-redeem-progress']);
    },
    onError: (error) => {
      const progress = error?.response?.data?.progress;
      if (progress) {
        queryClient.setQueryData(['first-redeem-progress'], progress);
      }
      if (error.response?.status === 403 && error.response?.data?.requiresOtp) {
        toast.error('Requiere verificación OTP para confirmar el retiro');
        setOtpStep('requested');
        return;
      }
      toast.error(getBackendErrorMessage(error, 'Error al crear solicitud'));
    }
  });

  const requestOtp = async () => {
    try {
      const channel = user?.tg_id ? 'telegram' : 'email';
      if (channel === 'email' && !user?.email) {
        toast.error('Configura un email o vincula Telegram para recibir códigos OTP');
        return;
      }
      await axios.post('/api/auth/otp/request', { channel, purpose: 'withdrawal_request' });
      toast.success(`Código enviado por ${channel === 'telegram' ? 'Telegram' : 'Email'}`);
      setOtpStep('requested');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Demasiadas solicitudes. Intenta de nuevo en 1 hora.');
      } else {
        toast.error(getBackendErrorMessage(error, 'Error al solicitar código'));
      }
    }
  };

  const verifyOtpAndSubmit = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Ingresa el código de 6 dígitos');
      return;
    }
    try {
      setOtpStep('verifying');
      await axios.post('/api/auth/otp/verify', { code: otpCode, purpose: 'withdrawal_request' });
      // OTP verified, now submit withdrawal
      redeemMutation.mutate({
        ...redeemData,
        payout_method: payoutMethod
      });
    } catch (error) {
      setOtpStep('requested');
      toast.error(getBackendErrorMessage(error, 'Código OTP inválido o expirado'));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!redeemData.cedula || !redeemData.telefono) {
      toast.error('Cédula y teléfono son requeridos');
      return;
    }

    if (payoutMethod === 'bs') {
      if (!redeemData.bank_code) {
        toast.error('Selecciona un banco');
        return;
      }
    } else {
      if (!redeemData.wallet_address) {
        toast.error('La dirección de wallet es requerida');
        return;
      }
    }

    if (otpStep !== 'verifying') {
      // First step: request OTP
      requestOtp();
      return;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="card-glass w-full max-w-md max-h-[95vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <h3 className="text-xl font-bold">Solicitar Retiro</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="space-y-4 overflow-y-auto pr-2 flex-1 scrollbar-thin">
              <div>
                <label className="block text-sm text-text/60 mb-1">Cantidad de Fuegos *</label>
                <input
                  type="number"
                  value={redeemData.fires_amount}
                  onChange={(e) => setRedeemData({...redeemData, fires_amount: e.target.value})}
                  min={payoutMethod === 'usdt_tron' ? 100 : 100}
                  step="1"
                  className="input-glass w-full"
                  required
                />
                <p className="text-[11px] text-text/40 mt-1">
                  {payoutMethod === 'usdt_tron'
                    ? 'Mínimo: 100 fuegos (retiro en USD)'
                    : 'Mínimo: 100 fuegos'}
                </p>
              </div>
              
              <div className="bg-white/5 rounded-2xl p-4 border border-white/10 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-text/60">Cantidad a retirar:</span>
                  <span className="text-text font-bold">{amountNumber.toFixed(2)} 🔥</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text/60">Comisión plataforma (5%):</span>
                  <span className="text-warning font-bold">{commissionAmount.toFixed(2)} 🔥</span>
                </div>
                <div className="border-t border-white/10 pt-3 flex justify-between">
                  <span className="text-text font-black">Total a deducir:</span>
                  <span className="text-fire-orange font-black text-lg">{totalRequiredAmount.toFixed(2)} 🔥</span>
                </div>
              </div>
              
              <div className="flex bg-white/5 rounded-xl p-1 text-sm border border-white/10">
                <button
                  type="button"
                  onClick={() => setPayoutMethod('bs')}
                  className={`flex-1 py-2.5 rounded-lg transition-all ${
                    payoutMethod === 'bs'
                      ? 'bg-gradient-to-r from-fire-orange to-fire-yellow text-dark font-black'
                      : 'text-text/60 hover:text-text hover:bg-white/5'
                  }`}
                >
                  Bolívares (Bs)
                </button>
                <button
                  type="button"
                  onClick={() => setPayoutMethod('usdt_tron')}
                  className={`flex-1 py-2.5 rounded-lg transition-all ${
                    payoutMethod === 'usdt_tron'
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-400 text-dark font-black'
                      : 'text-text/60 hover:text-text hover:bg-white/5'
                  }`}
                >
                  USDT (TRON)
                </button>
              </div>

              {payoutMethod === 'bs' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Cédula</label>
                      <input
                        type="text"
                        value={redeemData.cedula}
                        onChange={(e) => setRedeemData({...redeemData, cedula: e.target.value})}
                        placeholder="V12345678"
                        className="input-glass w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Teléfono</label>
                      <input
                        type="tel"
                        value={redeemData.telefono}
                        onChange={(e) => setRedeemData({...redeemData, telefono: e.target.value})}
                        placeholder="+58412..."
                        className="input-glass w-full"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Banco</label>
                    <select
                      value={redeemData.bank_code}
                      onChange={(e) => {
                        const nextCode = String(e.target.value || '').trim();
                        const bank = banksSorted.find((b) => String(b.code) === nextCode) || null;
                        setRedeemData({
                          ...redeemData,
                          bank_code: nextCode,
                          bank_name: bank ? String(bank.name) : ''
                        });
                      }}
                      className="input-glass w-full"
                      required
                    >
                      <option value="">Selecciona un banco…</option>
                      {banksSorted.map((bank) => (
                        <option key={bank.code} value={bank.code}>
                          {bank.code} - {bank.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Cédula</label>
                      <input
                        type="text"
                        value={redeemData.cedula}
                        onChange={(e) => setRedeemData({...redeemData, cedula: e.target.value})}
                        placeholder="V12345678"
                        className="input-glass w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Teléfono</label>
                      <input
                        type="tel"
                        value={redeemData.telefono}
                        onChange={(e) => setRedeemData({...redeemData, telefono: e.target.value})}
                        placeholder="+58412..."
                        className="input-glass w-full"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-text/50 mb-1 uppercase tracking-tighter font-bold">Wallet Address (TRC-20)</label>
                    <input
                      type="text"
                      value={redeemData.wallet_address}
                      onChange={(e) => setRedeemData({...redeemData, wallet_address: e.target.value})}
                      placeholder="TL..."
                      className="input-glass w-full"
                      required
                    />
                  </div>
                </>
              )}

              {!hasPayoutData && (
                <div className="bg-info/10 border border-info/20 rounded-xl p-3 flex gap-3">
                  <AlertCircle size={18} className="text-info flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-info leading-normal">
                    💡 Puedes guardar tus datos de retiro en <strong>Mis Datos → Datos de Retiro</strong> para prellenar automáticamente en futuras solicitudes.
                  </p>
                </div>
              )}

              <div className="bg-fire-orange/10 border border-fire-orange/20 rounded-xl p-3 flex gap-3">
                <AlertCircle size={20} className="text-fire-orange flex-shrink-0" />
                <div className="text-[11px] text-text/70 leading-normal">
                  {payoutMethod === 'usdt_tron' ? (
                    <>
                      Recibirás aproximadamente <span className="font-bold text-text">{estimatedUsdt.toFixed(2)} USD</span>.
                      La tasa es 1 USD ≈ {firesPerUsdt} 🔥.
                    </>
                  ) : (
                    <>
                      Recibirás <span className="font-bold text-text">{estimatedBs?.toFixed(2) || '---'} Bs</span>.
                      {bcvRate && ` Tasa BCV: ${parseFloat(bcvRate).toFixed(2)} Bs.`}
                    </>
                  )}
                  {" "}El proceso puede tardar hasta 48 horas hábiles.
                </div>
              </div>

              {/* OTP Verification Section */}
              {otpStep !== 'idle' && (
                <div className="p-4 rounded-xl bg-accent/10 border border-accent/20 space-y-3">
                  <p className="text-sm font-medium text-accent">Verificación de seguridad</p>
                  <p className="text-xs text-text/60">
                    Ingresa el código OTP de 6 dígitos enviado a tu {user?.tg_id ? 'Telegram' : 'Email'}.
                  </p>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    inputMode="numeric"
                    className="input-glass w-full text-center tracking-widest font-mono"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setOtpStep('idle'); setOtpCode(''); }}
                      className="flex-1 px-3 py-2 bg-glass hover:bg-glass-hover rounded-lg transition-colors text-xs"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={verifyOtpAndSubmit}
                      disabled={otpCode.length !== 6 || redeemMutation.isPending}
                      className="flex-1 px-3 py-2 bg-success hover:bg-success/90 text-white rounded-lg transition-colors font-semibold text-xs disabled:opacity-50"
                    >
                      {redeemMutation.isPending ? 'Procesando...' : 'Verificar y Confirmar'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-white/10 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-white/5 text-text py-3 rounded-xl hover:bg-white/10 transition-colors font-bold"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={redeemMutation.isPending || otpStep !== 'idle'}
                className="flex-1 bg-gradient-to-r from-fire-orange to-fire-yellow text-dark py-3 rounded-xl font-black shadow-fire hover:scale-[1.02] transition-transform disabled:opacity-50"
              >
                {otpStep !== 'idle'
                  ? 'Esperando OTP...'
                  : (redeemMutation.isPending ? 'Procesando...' : 'Confirmar Retiro')
                }
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RedeemModal;
